import { isAbortError } from "features/shared/lib/axios";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import type { RunExecutionParams } from "../../model/types";

import { invalidateExecutionQueries, runExecution } from "../ifcPipeline";

export const useRunExecution = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const abortRef = useRef<AbortController | null>(null);
  const [uploadProgress, setUploadProgress] = useState<null | number>(null);

  const clearUploadProgress = useCallback(() => {
    setUploadProgress(null);
  }, []);

  const { isLoading, mutateAsync, reset } = useMutation(
    async (variables: RunExecutionParams) => {
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;
      setUploadProgress(0);

      try {
        return await runExecution({
          ...variables,
          onUploadProgress: ({ percentage }) => setUploadProgress(percentage),
          signal: abortController.signal,
        });
      } finally {
        if (abortRef.current === abortController) {
          abortRef.current = null;
        }
      }
    },
    {
      onError: (error: unknown) => {
        clearUploadProgress();
        if (isAbortError(error)) return;
        const errorMessage = (error as { response?: { data?: unknown } })?.response?.data;
        toast.error(errorMessage ? JSON.stringify(errorMessage) : t("Execution.run.error"));
      },
      onSuccess: (_data: unknown, variables: RunExecutionParams) => {
        setUploadProgress(100);
        invalidateExecutionQueries(queryClient, variables.projectId);
        toast.success(t("Execution.run.success"));
        clearUploadProgress();
      },
    }
  );

  const cancelRun = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearUploadProgress();
    reset();
  }, [clearUploadProgress, reset]);

  return { cancelRun, isLoading, mutateAsync, uploadProgress };
};
